if (!customElements.get('brox-availability')) {
  class BroxAvailability extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.message = this.querySelector('.brox-availability__message');
      this.ack = this.querySelector('.brox-availability__ack');
      this.ackCopy = this.querySelector('.brox-availability__ack-copy');
      this.checkbox = this.querySelector('.brox-availability__checkbox');
      this.error = this.querySelector('.brox-availability__error');
      this.stockRequest = this.querySelector('.brox-stock-request');
      this.stockRequestToggle = this.querySelector('.brox-stock-request__toggle');
      this.stockRequestPanel = this.querySelector('.brox-stock-request__panel');
      this.stockPartText = this.querySelector('[data-brox-stock-part]');
      this.stockPartInput = this.querySelector('[data-brox-stock-part-input]');
      this.stockVariantInput = this.querySelector('[data-brox-stock-variant-input]');
      this.stockSubjectInput = this.querySelector('[data-brox-stock-subject-input]');
      this.stockBodyInput = this.querySelector('[data-brox-stock-body-input]');
      this.productData = JSON.parse(this.querySelector('.brox-availability__data').textContent);
      this.manufacturerRule = this.parseManufacturerRule(this.productData.manufacturerTag);
      this.builtToOrder = this.productData.builtToOrder === true;
      this.variants = new Map(this.productData.variants.map((variant) => [Number(variant.id), variant]));
      this.form = this.section && this.section.querySelector('.js-product-form-main');
      this.addButton = this.form && this.form.querySelector('[name="add"]');
      this.defaultAddButtonText = this.addButton ? this.addButton.dataset.addToCartText : 'Add to cart';
      this.cutoffTimer = null;

      this.checkbox.addEventListener('change', () => this.updateCartState());
      if (this.stockRequestToggle) {
        this.stockRequestToggle.addEventListener('click', () => {
          const expanded = this.stockRequestToggle.getAttribute('aria-expanded') === 'true';
          this.setStockRequestExpanded(!expanded);
        });
      }
      this.section.addEventListener('on:variant:change', (event) => {
        const variant = event.detail && event.detail.variant;
        const variantId = variant && this.variants.has(Number(variant.id))
          ? Number(variant.id)
          : this.getFormVariantId();
        this.render(variantId);
      });

      this.handleFormSubmit = (event) => {
        if (event.target !== this.form) return;
        if (!this.syncVariantFromForm()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        this.validateAcknowledgment(event);
      };

      this.handleFormData = (event) => {
        if (event.target !== this.form) return;
        const variantId = this.getFormVariantId();
        const variant = variantId ? this.variants.get(variantId) : null;
        if (!variant || variant.requiresShipping === false) {
          this.removeCartPropertiesFromFormData(event.formData);
          return;
        }
        const status = this.getStatus(variant);
        const timing = this.availabilityTiming(status);
        this.setCartPropertiesOnFormData(event.formData, timing.cartDate || timing.displayDate, status);
      };

      if (this.form) {
        this.section.addEventListener('submit', this.handleFormSubmit, true);
        this.form.addEventListener('formdata', this.handleFormData);
      }
      this.render(this.getFormVariantId() || this.productData.initialVariantId);
    }

    disconnectedCallback() {
      if (this.cutoffTimer) window.clearInterval(this.cutoffTimer);
      if (this.form && this.handleFormSubmit) {
        this.section.removeEventListener('submit', this.handleFormSubmit, true);
      }
      if (this.form && this.handleFormData) {
        this.form.removeEventListener('formdata', this.handleFormData);
      }
    }

    getStatus(variant) {
      const localStock = Number(variant.inventoryQuantity) || 0;
      const trackedByShopify = String(variant.inventoryManagement).toLowerCase() === 'shopify';
      const stopsAtZero = String(variant.inventoryPolicy).toLowerCase() === 'deny';

      if (trackedByShopify && localStock <= 0 && stopsAtZero) return this.statusConfig('discontinued', variant);
      if (this.builtToOrder) return this.statusConfig('built-to-order', variant);
      if (trackedByShopify && localStock > 0) return this.statusConfig('local', variant);
      if (Number(variant.marcorStock) > 0) return this.statusConfig('marcor', variant);
      if (Number(variant.jrpStock) > 0) return this.statusConfig('jrp', variant);
      if (Number(variant.turn14Stock) > 0) return this.statusConfig('turn14', variant);
      if (this.manufacturerRule) return this.statusConfig('manufacturer', variant);
      return this.statusConfig('check-availability', variant);
    }

    statusConfig(type, variant) {
      const warehouseTooltip = 'Available through our warehouse network. Same-day pickup is not available. The date shown includes the time needed before the order ships or is ready for pickup.';
      const configs = {
        local: {
          label: 'In stock locally',
          icon: 'store',
          tone: 'local',
          tooltip: 'Available at our Mississauga, Ontario location. Same-day pickup is typically available.',
          quantity: Number(variant.inventoryQuantity),
          quantityText: `${Number(variant.inventoryQuantity) === 1 ? 'unit' : 'units'} available at our Mississauga location.`,
          notice: 'Same-day pickup is typically available.',
          minDays: Number(this.dataset.localDays),
          maxDays: Number(this.dataset.localDays),
          requiresAck: true,
          source: 'Local'
        },
        marcor: {
          label: 'Warehouse stock',
          icon: 'warehouse',
          tone: 'warehouse',
          tooltip: warehouseTooltip,
          quantity: Number(variant.marcorStock),
          quantityText: `${Number(variant.marcorStock) === 1 ? 'unit' : 'units'} available through our warehouse network.`,
          notice: 'Not available for same-day pickup.',
          minDays: Number(this.dataset.marcorDays),
          maxDays: Number(this.dataset.marcorDays),
          requiresAck: true,
          source: 'Marcor'
        },
        jrp: {
          label: 'Warehouse stock',
          icon: 'warehouse',
          tone: 'warehouse',
          tooltip: warehouseTooltip,
          statusText: 'In stock',
          notice: 'Not available for same-day pickup.',
          minDays: Number(this.dataset.jrpDays),
          maxDays: Number(this.dataset.jrpDays),
          requiresAck: true,
          source: 'JRP'
        },
        turn14: {
          label: 'Warehouse stock',
          icon: 'warehouse',
          tone: 'warehouse',
          tooltip: warehouseTooltip,
          quantity: Number(variant.turn14Stock),
          quantityText: `${Number(variant.turn14Stock) === 1 ? 'unit' : 'units'} available through our warehouse network.`,
          notice: 'Not available for same-day pickup.',
          minDays: Number(this.dataset.turn14MinDays),
          maxDays: Number(this.dataset.turn14MaxDays),
          requiresAck: true,
          source: 'Turn14'
        },
        manufacturer: {
          label: 'Manufacturer stock',
          icon: 'factory',
          tone: 'manufacturer',
          tooltip: 'Available from the manufacturer. The estimated window includes the time needed to transfer the item before it ships or is ready for pickup.',
          statusText: 'Available from the manufacturer.',
          notice: '',
          minDays: this.manufacturerRule ? this.manufacturerRule.minDays : 0,
          maxDays: this.manufacturerRule ? this.manufacturerRule.maxDays : 0,
          requiresAck: true,
          source: 'Manufacturer'
        },
        'built-to-order': {
          label: 'Built to order',
          icon: 'wrench',
          tone: 'built',
          tooltip: 'This item is produced specifically for your order. The production timeframe is an estimate and may change.',
          statusText: 'Made specifically for your order.',
          notice: 'Production begins after your order is confirmed.',
          leadTimeText: '4–6 weeks',
          requiresAck: true,
          source: 'Built to order'
        },
        'check-availability': {
          label: 'Check availability',
          icon: 'search-package',
          tone: 'check',
          tooltip: 'Inventory is not connected for this item. You may order now, and our team will confirm availability and timing.',
          notice: 'Available to order — stock confirmation required.',
          requiresAck: true,
          source: 'Unconfirmed'
        },
        discontinued: {
          label: 'Discontinued',
          icon: 'package-off',
          tone: 'discontinued',
          tooltip: 'This product is no longer available and cannot be ordered.',
          notice: 'We no longer sell this product.',
          requiresAck: false,
          source: 'Discontinued'
        }
      };
      return { type, ...configs[type] };
    }

    render(variantId) {
      const variant = this.variants.get(Number(variantId));
      if (!variant) {
        this.hidden = true;
        return;
      }

      if (variant.requiresShipping === false) {
        this.hideForSoftware(variant);
        return;
      }

      this.hidden = false;
      this.variant = variant;
      this.status = this.getStatus(variant);
      this.dataset.tone = this.status.tone;
      const timing = this.availabilityTiming(this.status);
      const displayDate = timing.displayDate;
      const showQuantity = this.dataset.showQuantities === 'true' && Number(this.status.quantity) > 0;

      this.message.innerHTML = this.renderStatus(displayDate, showQuantity, timing);
      this.updateStockRequest(variant);
      this.checkbox.checked = false;
      this.error.hidden = true;
      this.ack.hidden = !this.status.requiresAck;
      this.ack.classList.remove('brox-availability__ack--error');
      this.ackCopy.textContent = this.status.type === 'check-availability'
        ? 'I understand that stock and timing are not yet confirmed and agree to proceed.'
        : ['manufacturer', 'built-to-order'].includes(this.status.type)
          ? 'I understand that the fulfilment timeframe is an estimate and agree to proceed with my order.'
          : 'I understand that this fulfillment date is an estimate and may change.';

      this.setCartProperties(timing.cartDate || displayDate);
      this.updateCartState();
      this.startCutoffTimer();
    }

    renderStatus(displayDate, showQuantity, timing = {}) {
      const status = this.status;
      const badge = `
        <div class="brox-availability__heading">
          <div class="brox-availability__badge brox-availability__badge--${status.tone}">
            <span class="brox-availability__badge-icon" aria-hidden="true">${this.icon(status.icon)}</span>
            <span>${this.escapeHtml(status.label)}</span>
          </div>
          <span class="brox-availability__info-wrap">
            <button class="brox-availability__info" type="button" aria-label="Availability information">i</button>
            <span class="brox-availability__tooltip" role="tooltip">${this.escapeHtml(status.tooltip)}</span>
          </span>
        </div>`;

      if (status.type === 'check-availability') {
        return `${badge}
          <p class="brox-availability__status-row"><strong>Available to order</strong><span> — stock confirmation required.</span></p>
          <p class="brox-availability__check-message">This item may be available from one of our nearby warehouses.</p>
          <p class="brox-availability__check-message brox-availability__check-message--action">Place your order now to reserve it, and we’ll confirm availability and timing.</p>`;
      }

      if (status.type === 'discontinued') {
        const email = this.escapeHtml(this.dataset.contactEmail || 'sales@broxtuning.ca');
        return `${badge}
          <p class="brox-availability__status-row"><strong>We no longer sell this product</strong></p>
          <p class="brox-availability__notice">This product can no longer be ordered.</p>
          <div class="brox-availability__support-card">
            <span class="brox-availability__panel-icon" aria-hidden="true">${this.icon('email')}</span>
            <span><span class="brox-availability__date-label">Looking for an alternative?</span>
            <strong>Contact our team</strong></span>
          </div>
          <p class="brox-availability__contact">Email <a href="mailto:${email}">${email}</a> with the part number and we’ll try to find a suitable replacement.</p>`;
      }

      if (status.type === 'built-to-order') {
        return `${badge}
          <p class="brox-availability__status-row"><strong>${this.escapeHtml(status.statusText)}</strong></p>
          <p class="brox-availability__notice">${this.escapeHtml(status.notice)}</p>
          <div class="brox-availability__date-card">
            <span class="brox-availability__panel-icon" aria-hidden="true">${this.icon('calendar-clock')}</span>
            <span><span class="brox-availability__date-label">Estimated production time</span>
            <strong>${this.escapeHtml(status.leadTimeText)}</strong></span>
          </div>
          <p class="brox-availability__transit-note">Delivery time varies based on your destination.</p>`;
      }

      if (status.type === 'marcor') {
        return `${badge}
          ${showQuantity ? `<p class="brox-availability__quantity"><strong>${this.formatQuantity(status.quantity)}</strong><span>${this.escapeHtml(status.quantityText)}</span></p>` : ''}
          <p class="brox-availability__notice">${this.escapeHtml(status.notice)}</p>
          <div class="brox-availability__date-card brox-availability__date-card--split">
            <div class="brox-availability__date-row">
              <span class="brox-availability__panel-icon" aria-hidden="true">${this.icon('truck')}</span>
              <span><span class="brox-availability__date-label">Shipping orders</span>
              <strong>Expected to ship ${this.escapeHtml(timing.shipDate || '')}</strong></span>
            </div>
            <div class="brox-availability__date-row">
              <span class="brox-availability__panel-icon" aria-hidden="true">${this.icon('store')}</span>
              <span><span class="brox-availability__date-label">Local pickup orders</span>
              <strong>Expected to be ready ${this.escapeHtml(timing.pickupDate || '')}</strong></span>
            </div>
          </div>
          ${timing.countdown ? `<p class="brox-availability__cutoff"><span aria-hidden="true">${this.icon('clock')}</span><span>Order within <strong>${this.escapeHtml(timing.countdown)}</strong> to have it ship by <strong>${this.escapeHtml(timing.shipDate)}</strong> or be ready for pickup by <strong>${this.escapeHtml(timing.pickupDate)}.</strong></span></p>` : ''}
          <p class="brox-availability__transit-note">Delivery time varies based on your destination.</p>`;
      }

      return `${badge}
        ${showQuantity ? `<p class="brox-availability__quantity"><strong>${this.formatQuantity(status.quantity)}</strong><span>${this.escapeHtml(status.quantityText)}</span></p>` : ''}
        ${status.statusText ? `<p class="brox-availability__status-row"><strong>${this.escapeHtml(status.statusText)}</strong></p>` : ''}
        ${status.notice ? `<p class="brox-availability__notice">${this.escapeHtml(status.notice)}</p>` : ''}
        <div class="brox-availability__date-card">
          <span class="brox-availability__panel-icon" aria-hidden="true">${this.icon('calendar')}</span>
          <span><span class="brox-availability__date-label">Expected to ship or be ready for pickup</span>
          <strong>${this.escapeHtml(displayDate)}</strong></span>
        </div>
        ${status.type === 'jrp' && timing.countdown ? `<p class="brox-availability__cutoff"><span aria-hidden="true">${this.icon('clock')}</span><span>Order within <strong>${this.escapeHtml(timing.countdown)}</strong> to have it ready to ship or pick up by <strong>${this.escapeHtml(displayDate)}.</strong></span></p>` : ''}
        <p class="brox-availability__transit-note">Delivery time varies based on your destination.</p>`;
    }

    availabilityTiming(status, now = new Date()) {
      if (!status || status.minDays === undefined) return { displayDate: '', cartDate: '' };

      if (status.type === 'marcor' || status.type === 'jrp') {
        const eastern = this.easternTime(now);
        const cutoffMinutes = status.type === 'marcor' ? 14 * 60 : 18 * 60;
        const beforeCutoff = eastern.isBusinessDay && eastern.minutes < cutoffMinutes;
        const countdown = beforeCutoff
          ? this.formatCountdown(Math.max(1, Math.ceil((cutoffMinutes * 60 - eastern.seconds) / 60)))
          : '';

        if (status.type === 'marcor') {
          const ship = this.addBusinessDaysFrom(eastern.date, beforeCutoff ? 0 : 1);
          const pickup = this.addBusinessDaysFrom(eastern.date, beforeCutoff ? 1 : 2);
          const shipDate = this.formatDateRange(ship, ship, 'UTC');
          const pickupDate = this.formatDateRange(pickup, pickup, 'UTC');
          return {
            displayDate: shipDate,
            shipDate,
            pickupDate,
            countdown,
            cartDate: `Shipping: ${shipDate}; local pickup: ${pickupDate}`
          };
        }

        const leadDays = Math.max(0, Number(status.minDays) || 0) + (beforeCutoff ? 0 : 1);
        const ready = this.addBusinessDaysFrom(eastern.date, leadDays);
        const readyDate = this.formatDateRange(ready, ready, 'UTC');
        return { displayDate: readyDate, countdown, cartDate: readyDate };
      }

      const dates = this.dateRange(status.minDays, status.maxDays);
      const displayDate = this.formatDateRange(dates.start, dates.end);
      return { displayDate, cartDate: displayDate };
    }

    startCutoffTimer() {
      if (this.cutoffTimer) window.clearInterval(this.cutoffTimer);
      this.cutoffTimer = null;
      if (!this.status || !['marcor', 'jrp'].includes(this.status.type)) return;

      this.cutoffTimer = window.setInterval(() => {
        if (!this.isConnected || !this.status || !['marcor', 'jrp'].includes(this.status.type)) return;
        const timing = this.availabilityTiming(this.status);
        const showQuantity = this.dataset.showQuantities === 'true' && Number(this.status.quantity) > 0;
        this.message.innerHTML = this.renderStatus(timing.displayDate, showQuantity, timing);
        this.setCartProperties(timing.cartDate || timing.displayDate);
      }, 30000);
    }

    setCartProperties(displayDate) {
      if (!this.form) return;
      const properties = this.getCartProperties(displayDate);
      Object.entries(properties).forEach(([name, value]) => {
        const fieldName = `properties[${name}]`;
        let input = Array.from(this.form.querySelectorAll('input[type="hidden"]')).find((field) => field.name === fieldName);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = fieldName;
          input.dataset.broxAvailabilityProperty = 'true';
          this.form.appendChild(input);
        }
        input.value = value;
        input.disabled = this.status.type === 'discontinued';
      });
    }

    getCartProperties(displayDate, status = this.status) {
      return {
        Availability: status.label,
        'Estimated ship / pickup date': displayDate || status.leadTimeText || 'To be confirmed',
        'Estimated date acknowledged': status.requiresAck ? 'Yes' : 'Not required',
        '_Inventory source': status.source
      };
    }

    setCartPropertiesOnFormData(formData, displayDate, status = this.status) {
      this.removeCartPropertiesFromFormData(formData);
      if (!status || status.type === 'discontinued') return;
      Object.entries(this.getCartProperties(displayDate, status)).forEach(([name, value]) => {
        formData.set(`properties[${name}]`, value);
      });
    }

    removeCartPropertiesFromFormData(formData) {
      [
        'Availability',
        'Estimated ship / pickup date',
        'Estimated date acknowledged',
        '_Inventory source'
      ].forEach((name) => formData.delete(`properties[${name}]`));
    }

    updateStockRequest(variant) {
      if (!this.stockRequest) return;
      const isCheckAvailability = this.status.type === 'check-availability';
      this.stockRequest.hidden = !isCheckAvailability;
      if (!isCheckAvailability) {
        this.setStockRequestExpanded(false);
        return;
      }

      const hasFeedback = Boolean(this.stockRequest.querySelector('.brox-stock-request__success, .brox-stock-request__form-error'));
      this.setStockRequestExpanded(hasFeedback);

      const partNumber = String(variant.sku || '').trim() || 'Not provided';
      const variantTitle = String(variant.title || '').trim() || 'Default';
      if (this.stockPartText) this.stockPartText.textContent = partNumber;
      if (this.stockPartInput) this.stockPartInput.value = partNumber;
      if (this.stockVariantInput) this.stockVariantInput.value = variantTitle;
      if (this.stockSubjectInput) this.stockSubjectInput.value = `Stock check request — Part # ${partNumber}`;
      if (this.stockBodyInput) {
        this.stockBodyInput.value = `Please confirm availability and the estimated fulfilment time for Part # ${partNumber} (${variantTitle}).`;
      }
    }

    setStockRequestExpanded(expanded) {
      if (!this.stockRequestToggle || !this.stockRequestPanel) return;
      this.stockRequestToggle.setAttribute('aria-expanded', String(expanded));
      this.stockRequestPanel.hidden = !expanded;
    }

    updateCartState() {
      if (!this.addButton || !this.variant || !this.status) return;
      const discontinued = this.status.type === 'discontinued';
      const unavailable = !this.variant.available;
      const needsConfirmation = this.status.requiresAck && !this.checkbox.checked;
      const disabled = discontinued || unavailable;

      this.addButton.disabled = disabled;
      this.addButton.setAttribute('aria-disabled', String(disabled));
      this.addButton.textContent = discontinued ? 'Discontinued' : this.defaultAddButtonText;

      const paymentButton = this.form && this.form.querySelector('.shopify-payment-button');
      if (paymentButton) paymentButton.hidden = discontinued || needsConfirmation || unavailable;
      if (this.checkbox.checked) {
        this.error.hidden = true;
        this.ack.classList.remove('brox-availability__ack--error');
      }
    }

    hideForSoftware(variant) {
      this.hidden = true;
      this.variant = variant;
      this.status = null;
      this.message.innerHTML = '';
      if (this.stockRequest) this.stockRequest.hidden = true;
      this.ack.hidden = true;
      this.checkbox.checked = false;
      this.removeAttribute('data-tone');

      if (this.form) {
        this.form.querySelectorAll('[data-brox-availability-property="true"]').forEach((input) => { input.disabled = true; });
        const paymentButton = this.form.querySelector('.shopify-payment-button');
        if (paymentButton) paymentButton.hidden = !variant.available;
      }

      if (this.addButton) {
        this.addButton.disabled = !variant.available;
        this.addButton.setAttribute('aria-disabled', String(!variant.available));
        if (variant.available) this.addButton.textContent = this.defaultAddButtonText;
      }
    }

    validateAcknowledgment(event) {
      if (!this.status || !this.status.requiresAck || this.checkbox.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.error.hidden = false;
      this.ack.classList.add('brox-availability__ack--error');
      this.ack.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.checkbox.focus();
    }

    getFormVariantId() {
      if (!this.form) return null;
      const variantInput = this.form.querySelector('[name="id"]');
      if (!variantInput) return null;
      const variantId = Number(variantInput.value);
      return Number.isFinite(variantId) && this.variants.has(variantId) ? variantId : null;
    }

    syncVariantFromForm() {
      const variantId = this.getFormVariantId();
      if (!variantId) return false;

      if (!this.variant || Number(this.variant.id) !== variantId) {
        this.render(variantId);
      } else if (this.status) {
        const timing = this.availabilityTiming(this.status);
        this.setCartProperties(timing.cartDate || timing.displayDate);
      }

      return Boolean(this.variant && Number(this.variant.id) === variantId
        && (this.variant.requiresShipping === false || this.status));
    }

    formatQuantity(quantity) {
      const cap = Number(this.dataset.quantityCap) || 10;
      return Number(quantity) >= cap ? `${cap}+` : String(quantity);
    }

    parseManufacturerRule(tag) {
      const match = String(tag || '').trim().match(/^manufacturer-stock:(\d+)-(\d+)$/i);
      if (!match) return null;
      const minDays = Number(match[1]);
      const maxDays = Number(match[2]);
      if (maxDays < minDays) return null;
      return { minDays, maxDays };
    }

    dateRange(minDays, maxDays) {
      const startDays = Math.max(0, Number(minDays) || 0);
      const endDays = Math.max(startDays, Number(maxDays) || 0);
      return {
        start: this.addBusinessDays(startDays),
        end: this.addBusinessDays(endDays)
      };
    }

    easternTime(now = new Date()) {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23'
      });
      const parts = Object.fromEntries(formatter.formatToParts(now)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number(part.value)]));
      const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      const day = date.getUTCDay();
      return {
        date,
        isBusinessDay: day !== 0 && day !== 6,
        minutes: parts.hour * 60 + parts.minute,
        seconds: parts.hour * 3600 + parts.minute * 60 + parts.second
      };
    }

    addBusinessDaysFrom(baseDate, days) {
      const date = new Date(baseDate.getTime());
      let remaining = Math.max(0, Number(days) || 0);
      if (remaining === 0) {
        while (date.getUTCDay() === 0 || date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() + 1);
        return date;
      }
      while (remaining > 0) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6) remaining -= 1;
      }
      return date;
    }

    formatCountdown(totalMinutes) {
      const minutes = Math.max(1, Number(totalMinutes) || 1);
      const hours = Math.floor(minutes / 60);
      const remainder = minutes % 60;
      const parts = [];
      if (hours) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
      if (remainder) parts.push(`${remainder} ${remainder === 1 ? 'min' : 'mins'}`);
      return parts.join(' ');
    }

    addBusinessDays(days) {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      let remaining = Math.max(0, Number(days) || 0);

      if (remaining === 0) {
        while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
      } else {
        while (remaining > 0) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 0 && date.getDay() !== 6) remaining -= 1;
        }
      }
      return date;
    }

    formatDateRange(start, end, timeZone) {
      const format = (date, options) => new Intl.DateTimeFormat('en-CA', timeZone ? { ...options, timeZone } : options).format(date);
      const year = (date) => timeZone === 'UTC' ? date.getUTCFullYear() : date.getFullYear();
      const month = (date) => timeZone === 'UTC' ? date.getUTCMonth() : date.getMonth();
      if (start.getTime() === end.getTime()) {
        return format(start, { year: 'numeric', month: 'long', day: 'numeric' });
      }
      if (year(start) === year(end) && month(start) === month(end)) {
        return `${format(start, { month: 'long', day: 'numeric' })}–${format(end, { day: 'numeric' })}, ${year(end)}`;
      }
      return `${format(start, { month: 'short', day: 'numeric' })}–${format(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }

    icon(name) {
      const icons = {
        store: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v11h16V9M3 9l2-5h14l2 5M8 20v-6h8v6M3 9c0 2 3 2 3 0 0 2 3 2 3 0 0 2 3 2 3 0 0 2 3 2 3 0 0 2 3 2 3 0"/></svg>',
        truck: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h11v10H3V6Zm11 4h4l3 3v3h-7v-6ZM7 20a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>',
        clock: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
        warehouse: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21V8l9-5 9 5v13M7 21V10h10v11M9 13h6M9 16h6M9 19h6"/></svg>',
        factory: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 21V9l6 3V8l6 4V5h6v16H3ZM7 17h2M12 17h2M17 17h2"/></svg>',
        wrench: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.7 6.3a4 4 0 0 0-5-5L12 3.6 8.4 7.2 6.1 4.9a4 4 0 0 0 5 5L4 17a2.1 2.1 0 0 0 3 3l7.1-7.1a4 4 0 0 0 5-5l-2.3 2.3-3.6-3.6 1.5-1.3Z"/></svg>',
        calendar: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3v3M19 3v3M3 9h18M5 5h14a2 2 0 0 1 2 2v14H3V7a2 2 0 0 1 2-2ZM7 13h2M11 13h2M15 13h2M7 17h2M11 17h2M15 17h2"/></svg>',
        'calendar-clock': '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3v3M19 3v3M3 9h18M5 5h14a2 2 0 0 1 2 2v5M3 7v14h9M16 18a4 4 0 1 0 8 0 4 4 0 0 0-8 0Zm4-2v2l1.5 1"/></svg>',
        'search-package': '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 7 6-3 6 3-6 3-6-3Zm0 0v7l6 3 2-1M16 7v5M15 17a4 4 0 1 0 8 0 4 4 0 0 0-8 0Zm7 3 2 2"/></svg>',
        'package-off': '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 7 7-4 7 4v10l-7 4-7-4V7Zm0 0 7 4 7-4M12 11v10M3 3l18 18"/></svg>',
        email: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18v14H3V5Zm1 1 8 7 8-7"/></svg>',
        'email-clock': '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h15v10H3V5Zm1 1 7 6 6-5M16 18a4 4 0 1 0 8 0 4 4 0 0 0-8 0Zm4-2v2l1.5 1"/></svg>'
      };
      return icons[name] || '';
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define('brox-availability', BroxAvailability);
}
